/**
 * @fileoverview Test Calendar API Service
 * @description Service for interacting with Test Calendar API through Python backend
 * @module services/api/calendar
 */

import { apiClient } from './client';

const BASE = '/v1/test-calendar';

/**
 * Test Calendar API Service
 * Provides methods to interact with Test Calendar data through the backend API
 */
export const calendarApi = {

  // ═══════════════════════════════════════════════════
  //  TEAMS
  // ═══════════════════════════════════════════════════

  getTeams: async (includeInactive = false) => {
    return apiClient.get(`${BASE}/teams`, { include_inactive: includeInactive });
  },

  getTeam: async (id) => {
    return apiClient.get(`${BASE}/teams/${id}`);
  },

  createTeam: async (data) => {
    return apiClient.post(`${BASE}/teams`, data);
  },

  updateTeam: async (id, data) => {
    return apiClient.put(`${BASE}/teams/${id}`, data);
  },

  deleteTeam: async (id) => {
    return apiClient.delete(`${BASE}/teams/${id}`);
  },

  // ═══════════════════════════════════════════════════
  //  ENVIRONMENTS
  // ═══════════════════════════════════════════════════

  getEnvironments: async (includeInactive = false) => {
    return apiClient.get(`${BASE}/environments`, { include_inactive: includeInactive });
  },

  getEnvironment: async (id) => {
    return apiClient.get(`${BASE}/environments/${id}`);
  },

  createEnvironment: async (data) => {
    return apiClient.post(`${BASE}/environments`, data);
  },

  updateEnvironment: async (id, data) => {
    return apiClient.put(`${BASE}/environments/${id}`, data);
  },

  deleteEnvironment: async (id) => {
    return apiClient.delete(`${BASE}/environments/${id}`);
  },

  // ═══════════════════════════════════════════════════
  //  TEST TYPES
  // ═══════════════════════════════════════════════════

  getTestTypes: async (includeInactive = false) => {
    return apiClient.get(`${BASE}/test-types`, { include_inactive: includeInactive });
  },

  getTestType: async (id) => {
    return apiClient.get(`${BASE}/test-types/${id}`);
  },

  createTestType: async (data) => {
    return apiClient.post(`${BASE}/test-types`, data);
  },

  updateTestType: async (id, data) => {
    return apiClient.put(`${BASE}/test-types/${id}`, data);
  },

  deleteTestType: async (id) => {
    return apiClient.delete(`${BASE}/test-types/${id}`);
  },

  // ═══════════════════════════════════════════════════
  //  CALENDAR EVENTS — CRUD
  // ═══════════════════════════════════════════════════

  /**
   * Get events with optional filters
   * @param {Object} filters - { start_date, end_date, status, priority, team_id, test_type_id, environment_id, created_by, search, page, page_size }
   */
  getEvents: async (filters = {}) => {
    return apiClient.get(`${BASE}/events`, filters);
  },

  getEvent: async (id) => {
    return apiClient.get(`${BASE}/events/${id}`);
  },

  /**
   * Create a new calendar event
   * @param {Object} data - { title, start_date, end_date, created_by, ... }
   */
  createEvent: async (data) => {
    return apiClient.post(`${BASE}/events`, data);
  },

  updateEvent: async (id, data) => {
    return apiClient.put(`${BASE}/events/${id}`, data);
  },

  deleteEvent: async (id, deletedBy, deletedByName) => {
    return apiClient.delete(`${BASE}/events/${id}`, {
      deleted_by: deletedBy,
      deleted_by_name: deletedByName,
    });
  },

  // ═══════════════════════════════════════════════════
  //  EVENT ACTIONS — Status, Extend, Close
  // ═══════════════════════════════════════════════════

  changeEventStatus: async (id, status, changedBy, changedByName) => {
    return apiClient.patch(`${BASE}/events/${id}/status`, {
      status,
      changed_by: changedBy,
      changed_by_name: changedByName,
    });
  },

  extendEvent: async (id, newEndDate, extendedBy, extendedByName, reason) => {
    return apiClient.post(`${BASE}/events/${id}/extend`, {
      new_end_date: newEndDate,
      extended_by: extendedBy,
      extended_by_name: extendedByName,
      reason,
    });
  },

  closeEvent: async (id, status, closedBy, closedByName, reason) => {
    return apiClient.post(`${BASE}/events/${id}/close`, {
      status,
      closed_by: closedBy,
      closed_by_name: closedByName,
      reason,
    });
  },

  // ═══════════════════════════════════════════════════
  //  PARTICIPANTS
  // ═══════════════════════════════════════════════════

  getParticipants: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/participants`);
  },

  addParticipant: async (eventId, data) => {
    return apiClient.post(`${BASE}/events/${eventId}/participants`, data);
  },

  removeParticipant: async (eventId, participantId) => {
    return apiClient.delete(`${BASE}/events/${eventId}/participants/${participantId}`);
  },

  // ═══════════════════════════════════════════════════
  //  COMMENTS
  // ═══════════════════════════════════════════════════

  getComments: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/comments`);
  },

  createComment: async (eventId, data) => {
    return apiClient.post(`${BASE}/events/${eventId}/comments`, data);
  },

  updateComment: async (eventId, commentId, data) => {
    return apiClient.put(`${BASE}/events/${eventId}/comments/${commentId}`, data);
  },

  deleteComment: async (eventId, commentId) => {
    return apiClient.delete(`${BASE}/events/${eventId}/comments/${commentId}`);
  },

  // ═══════════════════════════════════════════════════
  //  ATTACHMENTS
  // ═══════════════════════════════════════════════════

  getAttachments: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/attachments`);
  },

  createAttachment: async (eventId, data) => {
    return apiClient.post(`${BASE}/events/${eventId}/attachments`, data);
  },

  deleteAttachment: async (eventId, attachmentId) => {
    return apiClient.delete(`${BASE}/events/${eventId}/attachments/${attachmentId}`);
  },

  // ═══════════════════════════════════════════════════
  //  EXTENSIONS HISTORY (read-only)
  // ═══════════════════════════════════════════════════

  getExtensions: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/extensions`);
  },

  // ═══════════════════════════════════════════════════
  //  AUDIT LOG (read-only)
  // ═══════════════════════════════════════════════════

  getAuditLog: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/audit-log`);
  },

  // ═══════════════════════════════════════════════════
  //  DASHBOARD / SUMMARY
  // ═══════════════════════════════════════════════════

  getSummary: async () => {
    return apiClient.get(`${BASE}/summary`);
  },

  // ═══════════════════════════════════════════════════
  //  EVENT SUBSCRIPTIONS
  // ═══════════════════════════════════════════════════

  /**
   * Get all subscribers for an event
   */
  getSubscriptions: async (eventId) => {
    return apiClient.get(`${BASE}/events/${eventId}/subscriptions`);
  },

  /**
   * Check if current user is subscribed to an event
   * @param {number} eventId
   * @param {string} userId
   * @returns {Promise<{is_subscribed: boolean, subscriber_count: number}>}
   */
  checkSubscription: async (eventId, userId) => {
    return apiClient.get(`${BASE}/events/${eventId}/subscriptions/check`, { user_id: userId });
  },

  /**
   * Subscribe to an event
   */
  subscribe: async (eventId, userId, userName) => {
    return apiClient.post(`${BASE}/events/${eventId}/subscriptions`, {
      user_id: userId,
      user_name: userName,
    });
  },

  /**
   * Unsubscribe from an event
   */
  unsubscribe: async (eventId, userId) => {
    return apiClient.delete(`${BASE}/events/${eventId}/subscriptions`, {
      user_id: userId,
    });
  },

  /**
   * Get all events the user is subscribed to
   */
  getUserSubscriptions: async (userId) => {
    return apiClient.get(`${BASE}/subscriptions/user`, { user_id: userId });
  },

  // ═══════════════════════════════════════════════════
  //  NOTIFICATIONS
  // ═══════════════════════════════════════════════════

  /**
   * Get notifications for the current user
   * @param {string} userId
   * @param {Object} options - { include_read, page, page_size }
   */
  getNotifications: async (userId, options = {}) => {
    return apiClient.get(`${BASE}/notifications`, {
      user_id: userId,
      include_read: options.includeRead || false,
      page: options.page || 1,
      page_size: options.pageSize || 20,
    });
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async (userId) => {
    return apiClient.get(`${BASE}/notifications/unread-count`, { user_id: userId });
  },

  /**
   * Mark a notification as read
   */
  markNotificationRead: async (notificationId, userId) => {
    return apiClient.patch(`${BASE}/notifications/${notificationId}/read`, {
      user_id: userId,
    });
  },

  /**
   * Mark all notifications as read
   */
  markAllNotificationsRead: async (userId) => {
    return apiClient.patch(`${BASE}/notifications/read-all`, {
      user_id: userId,
    });
  },
};

export default calendarApi;
